import { FC } from 'react';
import { Button, Link, Modal } from '@carbon/react';
import {
  Add,
  CheckmarkOutline,
  Close,
  CollapseAll,
  Edit,
  ExpandAll,
  Restart,
  TrashCan,
  Undo,
} from '@carbon/react/icons';
import { WellWorkflowStatusBadge } from './well-workflow-status-badge';
import type { WellSectionStatus } from './types';
import styles from './cost.module.scss';

interface Props {
  wellId: number;
  wellName?: string | null;
  wellNo?: number;
  status: WellSectionStatus;
  isEditing: boolean;
  isRemoved: boolean;
  isReviewed: boolean;
  canModify: boolean;
  dirty: boolean;
  saving: boolean;
  rerunning: boolean;
  /** Whether the expand/collapse-all toggle is in the "all expanded" state. */
  allExpanded: boolean;
  /** Whether any read-view buckets exist (gates the expand/collapse-all button). */
  hasBuckets: boolean;
  /** The staged AfeTarget id (gates the Remove Well button); null/undefined hides it. */
  afeTargetId: number | null | undefined;
  removeModalOpen: boolean;
  rerunModalOpen: boolean;
  /** First category option, used as the default for newly-added line items. */
  defaultAddCategory: string;
  onAddLineItem: (category: string) => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onUndoRemove: () => void;
  onRemoveCorrect: () => void;
  onToggleAll: () => void;
  onMarkCorrect: () => void;
  onEnterEditMode: () => void;
  onOpenRemoveModal: () => void;
  onCloseRemoveModal: () => void;
  onConfirmRemoveWell: () => void;
  onOpenRerunModal: () => void;
  onCloseRerunModal: () => void;
  onRerunExtraction: () => void;
}

export const StructureCostsHeader: FC<Props> = ({
  wellId,
  wellName,
  wellNo,
  status,
  isEditing,
  isRemoved,
  isReviewed,
  canModify,
  dirty,
  saving,
  rerunning,
  allExpanded,
  hasBuckets,
  afeTargetId,
  removeModalOpen,
  rerunModalOpen,
  defaultAddCategory,
  onAddLineItem,
  onCancelEdit,
  onSave,
  onUndoRemove,
  onRemoveCorrect,
  onToggleAll,
  onMarkCorrect,
  onEnterEditMode,
  onOpenRemoveModal,
  onCloseRemoveModal,
  onConfirmRemoveWell,
  onOpenRerunModal,
  onCloseRerunModal,
  onRerunExtraction,
}) => {
  return (
    <div className={styles.headerBar}>
      <div className={styles.headerTitleMeta}>
        <h4 className={styles.wellName}>
          {wellNo !== null && wellNo !== undefined && wellName
            ? `Well ${wellNo} • ${wellName}`
            : 'Structure Costs'}
        </h4>
        <span className={styles.afeId}>
          AFE ID: <Link href={`/afe_catalog/${wellId}`}>{wellId}</Link>
        </span>
        <WellWorkflowStatusBadge status={status} />
      </div>
      <div className={styles.headerActions}>
        {isEditing ? (
          <>
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Add}
              onClick={() => onAddLineItem(defaultAddCategory)}
            >
              Add Line Item
            </Button>
            <Button kind="secondary" size="sm" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button kind="primary" size="sm" disabled={saving} onClick={onSave}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </>
        ) : isRemoved ? (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Undo}
            onClick={onUndoRemove}
          >
            Undo Remove
          </Button>
        ) : isReviewed ? (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Close}
            onClick={onRemoveCorrect}
          >
            Remove Correct Status
          </Button>
        ) : (
          <>
            {hasBuckets && (
              <Button
                kind="tertiary"
                size="sm"
                hasIconOnly
                tooltipPosition="bottom"
                tooltipAlignment="end"
                renderIcon={allExpanded ? CollapseAll : ExpandAll}
                iconDescription={allExpanded ? 'Collapse All' : 'Expand All'}
                onClick={onToggleAll}
              />
            )}
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              tooltipPosition="bottom"
              tooltipAlignment="end"
              renderIcon={Restart}
              iconDescription="Rerun Cost Extraction"
              disabled={rerunning}
              onClick={onOpenRerunModal}
            />
            {canModify && (
              <Button
                kind="tertiary"
                size="sm"
                renderIcon={Edit}
                onClick={onEnterEditMode}
              >
                Edit Line Items
              </Button>
            )}
            <Button
              kind="primary"
              size="sm"
              renderIcon={CheckmarkOutline}
              disabled={dirty}
              onClick={onMarkCorrect}
            >
              Mark as Correct
            </Button>
          </>
        )}
        {!isEditing &&
          !isRemoved &&
          afeTargetId !== null &&
          afeTargetId !== undefined && (
            <Button
              kind="danger--tertiary"
              size="sm"
              renderIcon={TrashCan}
              onClick={onOpenRemoveModal}
            >
              Remove Well
            </Button>
          )}
      </div>
      <Modal
        open={removeModalOpen}
        danger
        size="sm"
        modalHeading="Remove well?"
        primaryButtonText="Remove Well"
        secondaryButtonText="Cancel"
        onRequestClose={onCloseRemoveModal}
        onRequestSubmit={onConfirmRemoveWell}
      >
        <p>
          This marks the well inactive in the catalog. You can undo it from this
          panel or the Well Information section.
        </p>
      </Modal>
      <Modal
        open={rerunModalOpen}
        danger
        size="sm"
        modalHeading="Rerun cost extraction?"
        primaryButtonText={rerunning ? 'Rerunning…' : 'Rerun Extraction'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={rerunning}
        onRequestClose={onCloseRerunModal}
        onRequestSubmit={onRerunExtraction}
      >
        <p>
          This re-derives the cost line items from the well&apos;s extracted
          data and replaces the current list. Any manual edits to line items
          (including revised totals) will be overwritten.
        </p>
      </Modal>
    </div>
  );
};
